(ns rothko.editor.document.component
  (:require [reagent.core :as r]
            [rothko.editor.actions :as actions]
            [rothko.editor.document.script :as script]
            [rothko.editor.edit.component :as edit]
            [rothko.editor.newline.component :as newline]
            [rothko.editor.punctuation.component :as punctuation]
            [rothko.editor.space.component :as space]
            [rothko.editor.tag.component :as tag]
            [rothko.editor.word.component :as word]))


;; At some point we may want to choose a subset of
;; tokens that an implementation supports. If so,
;; implementations should register their own token
;; types like they do for actions

(defn- token->component
  [{:keys [appearance corrected-view? !state] :as _props} {:keys [type start duration] :as token} misspelled? focus-at]
  (let [token-component (case type
                          (:space :space-edit) space/token
                          :word word/token
                          :edit edit/token
                          :punctuation punctuation/token
                          :newline newline/token
                          :tag tag/token
                          (throw (ex-info ":type not supported for: " {:token token})))
        focussed? (and start duration focus-at (< start focus-at (+ start duration)))]
    [token-component

     (condp contains? type
       #{:newline} {}

       #{:tag} {:appearance appearance, :token token}

       #{:space, :space-edit, :punctuation}
       {:!state !state
        :token (if corrected-view? token (script/assoc-suspicious token))
        :corrected-view? corrected-view?}

       #{:edit, :word}
       {:appearance (cond-> appearance focussed? (conj :focussed))
        :!state !state
        :token (cond-> token
                 corrected-view? (dissoc :marks)
                 (not corrected-view?) (assoc :misspelled? misspelled?)
                 (not corrected-view?) (script/assoc-suspicious))
        :corrected-view? corrected-view?}

       :else (throw (ex-info ":type not supported for: " {:token token})))]))

;; TODO the multi-stage transcription handling needs to be moved out of here and into the implementation
(defn document->components
  [{:keys [corrected-view? corrected-view-filter script-step spelling-dictionary] :as props} script focus-at]
  (let [view-token? (if corrected-view? corrected-view-filter identity)]
    (loop [script script
           res []]
      (if (empty? script)
        res
        (let [this-token (first script)
              next-token (second script)
              merge-contraction? (script/detect-contraction this-token next-token)
              misspelled? (false? (get spelling-dictionary
                                       (if merge-contraction?
                                         (str (:word this-token) (:word next-token))
                                         (:word this-token))))]
          (recur
           (nthrest script (cond-> (script-step
                                    (view-token? this-token)
                                    (second script))
                                   merge-contraction? inc))
           (cond-> res

                   (view-token? this-token)
                   (conj (token->component props this-token misspelled? focus-at))

                   (and (view-token? next-token) merge-contraction?)
                   (conj (token->component props next-token misspelled? focus-at)))))))))

(defn document
  [{:keys [!document !state after-render on-transform on-mount] :as _props}]
  (r/create-class
    {:component-did-mount
     (fn [] (when on-mount (on-mount @!document)))

     ;; It has to be prefixed 'unsafe' since React 17. Tried to replace it with `should-component-update`, but that was not called.
     ;; Maybe it wasn't a good idea anyway, since `should-component-update` is only an 'optimisation'.
     :UNSAFE_component-will-update
     (fn []
       (let [transformations (actions/transformations!)
             old-document @!document
             _ (script/apply-transformations! !document transformations)
             new-document @!document]
         (when (and on-transform
                    (not= (:script old-document)
                          (:script new-document)))
           (on-transform new-document))
         (r/after-render after-render)))

     :reagent-render
     (fn [{:keys [appearance ref] :as props}]
       (into [:p.editor__document
              {:class appearance
               :ref ref}]
         (let [{:keys [original-script script]} @!document
               {:keys [view focus-at]} @!state]
           (document->components (assoc props :corrected-view? (= view :corrected))
                                 (if (= :original view) original-script script)
                                 focus-at))))}))

(defn ro-document
  "The read-only view of the document being annotated."
  [{:keys [!document !state on-transform on-mount] :as _props}]
  (r/create-class
   {:component-did-mount   (fn []
                             (when on-mount
                               (on-mount @!document)))

    :UNSAFE_component-will-update (fn []
                                    (let [transformations (actions/transformations!)
                                          old-document    @!document
                                          _               (script/apply-transformations! !document transformations)
                                          new-document    @!document]
                                      (when (and on-transform
                                                 (not= (:script old-document)
                                                       (:script new-document)))
                                        (on-transform new-document))))

    :reagent-render        (fn [{:keys [appearance ref]
                                 :as   props}]
                             (let [{:keys [ro-view focus-at]} @!state]
                               (into [:p.editor__document
                                      {:class appearance
                                       :ref   ref}]
                                     (let [{:keys [original-script script]} @!document]
                                       (document->components (assoc props :corrected-view? (= ro-view :ro-corrected))
                                                             (if (= :ro-original ro-view) original-script script)
                                                             focus-at)))))}))
